import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private successTimeout: any;
  private warningTimeout: any;

  constructor(private translateService: TranslateService) {}

  // Errors stay until the user closes them (no auto-close); success and warning close after 5 seconds.
  error(error: string) {
    if (error) {
      const formattedError = this.sanitizeErrorMessage(error);
      const translated = this.translateService?.instant
        ? this.translateService.instant(formattedError)
        : formattedError;
      const displayMessage = translated || formattedError;

      // Show the toast (an aria-live region) before writing the text, so screen readers announce it.
      const toasters = document.querySelectorAll(
        '.error_toaster, .error_toaster_show'
      );
      if (toasters.length > 0) {
        toasters[0].setAttribute('class', 'error_toaster_show');
      }

      const errorElement = document.getElementById('errorMsg');
      if (errorElement) {
        errorElement.innerHTML = displayMessage;
      }
    } else {
      const errorElement = document.getElementById('errorMsg');
      if (errorElement) {
        errorElement.innerHTML = '';
      }
      const showToasters = document.querySelectorAll('.error_toaster_show');
      showToasters.forEach((toaster) =>
        toaster.setAttribute('class', 'error_toaster')
      );
    }
  }

  success(message: string) {
    if (this.successTimeout) {
      clearTimeout(this.successTimeout);
      this.successTimeout = null;
    }

    if (message) {
      const translated = this.translateService?.instant
        ? this.translateService.instant(message)
        : message;
      const displayMessage = translated || message;

      const successElement = document.getElementById('successMsg');
      const toasters = document.querySelectorAll(
        '.success_toaster, .success_toaster_show'
      );
      // Show the toast (an aria-live region) before writing the text, so screen readers announce it.
      if (toasters.length > 0) {
        toasters[0].setAttribute('class', 'success_toaster_show');
      }
      if (successElement) {
        successElement.innerHTML = displayMessage;
      }

      if (toasters.length > 0) {
        this.successTimeout = setTimeout(() => {
          if (successElement) {
            successElement.innerHTML = '';
          }
          if (toasters.length > 0) {
            toasters[0].setAttribute('class', 'success_toaster');
          }
        }, 5000);
      }
    } else {
      const successElement = document.getElementById('successMsg');
      if (successElement) {
        successElement.innerHTML = '';
      }
      const showToasters = document.querySelectorAll('.success_toaster_show');
      showToasters.forEach((toaster) =>
        toaster.setAttribute('class', 'success_toaster')
      );
    }
  }

  warning(message: string) {
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
      this.warningTimeout = null;
    }

    if (message) {
      const translated = this.translateService?.instant
        ? this.translateService.instant(message)
        : message;
      const displayMessage = translated || message;

      const warningElement = document.getElementById('warningMsg');
      const toasters = document.querySelectorAll(
        '.warning_toaster, .warning_toaster_show'
      );
      // Show the toast (an aria-live region) before writing the text, so screen readers announce it.
      if (toasters.length > 0) {
        toasters[0].setAttribute('class', 'warning_toaster_show');
      }
      if (warningElement) {
        warningElement.innerHTML = displayMessage;
      }

      if (toasters.length > 0) {
        this.warningTimeout = setTimeout(() => {
          if (warningElement) {
            warningElement.innerHTML = '';
          }
          if (toasters.length > 0) {
            toasters[0].setAttribute('class', 'warning_toaster');
          }
        }, 5000);
      }
    } else {
      const warningElement = document.getElementById('warningMsg');
      if (warningElement) {
        warningElement.innerHTML = '';
      }
      const showToasters = document.querySelectorAll('.warning_toaster_show');
      showToasters.forEach((toaster) =>
        toaster.setAttribute('class', 'warning_toaster')
      );
    }
  }

  private sanitizeErrorMessage(error: string): string {
    if (typeof error !== 'string') return error;

    const lower = error.toLowerCase();
    const isOffline =
      (typeof navigator !== 'undefined' && !navigator.onLine) ||
      lower.includes('net::err_internet_disconnected') ||
      lower.includes('net::err_name_not_resolved');

    if (isOffline) {
      const isArabic =
        localStorage.getItem('language')?.toLowerCase().startsWith('ar') ||
        false;
      return isArabic
        ? 'لا يوجد اتصال بالإنترنت. يرجى التحقق من اتصالك بالشبكة والمحاولة مرة أخرى.'
        : 'No internet connection. Please check your network connection and try again.';
    }

    return error;
  }
}